import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader2 } from "lucide-react";
import PaymentMethodInfo from "components/settings/shared/billing-details/paymentMethodInfo";
import { useCustomSnackbars } from "components/snackbars/useCustomSnackbars";
import { User, useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { HubPlan } from "data/models";
import { ReactQueryMutationError, extractClip } from "data/mutations-common";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useIntercom } from "react-use-intercom";
import { toTitleCase } from "utils/commons";
import { resolveIdentityId } from "utils/identity";
import { ClipConfiguration, QuoteSummary } from "../../models";

interface PurchaseWithDollarsProps {
  clipConfig: ClipConfiguration;
  quoteSummary: QuoteSummary;
  plan: HubPlan;
  resetClipConfig: () => void;
  setSubStep: React.Dispatch<React.SetStateAction<number>>;
}

const formatDollars = (n: number) => {
  return n.toLocaleString("en-AU", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
};

export const PurchaseWithDollars = (props: PurchaseWithDollarsProps) => {
  const [identityState] = useIdentity();
  const { show } = useIntercom();
  const identityId = resolveIdentityId(identityState);
  const [paymentMethod, setPaymentMethod] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const calculateTaxExcluded = (amount: number) => {
    return amount - amount / 11;
  };

  const { enqueueMutationFailed } = useCustomSnackbars();

  const submitExtractJob = useMutation({
    mutationFn: (clipId: string) => extractClip(clipId, "invoice"),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeys.clipsKey, identityId],
      }),
        enqueueSnackbar("Clip has been submitted for extraction.", {
          variant: "success",
        });
      props.resetClipConfig();
    },
    onError: (error: Error) => {
      enqueueMutationFailed(error.toString());
    },
  });
  return (
    <>
      <div className="flex flex-col gap-4 flex-1 min-h-0 overflow-auto *:shrink-0">
        <div className="flex flex-col gap-1 mt-4">
          <Label>Contact Email</Label>
          <Input
            disabled
            value={(identityState as User).email}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label>Contact Phone</Label>
          <Input disabled value="" />
        </div>
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="font-bold px-0 w-2/5">Description</TableHead>
              <TableHead className="font-bold px-0 text-right w-[15%]">Qty</TableHead>
              <TableHead className="font-bold px-0 text-right w-1/5">Unit Price</TableHead>
              <TableHead className="font-bold px-0 text-right w-1/4">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            <TableRow>
              <TableCell className="px-0 whitespace-normal wrap-break-word">
                {`${toTitleCase(props.plan.product_display_name)} Plan Credits`}
              </TableCell>
              <TableCell className="px-0 text-right">
                {props.quoteSummary.totalCredits.toLocaleString()}
              </TableCell>
              <TableCell className="px-0 text-right">
                {props.plan.overage_cost_dollars}
              </TableCell>
              <TableCell className="px-0 text-right">
                {`$${formatDollars(props.quoteSummary.dollarCost)}`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="px-0" />
              <TableCell className="px-0" />
              <TableCell className="px-0 text-right">Sub Total</TableCell>
              <TableCell className="px-0 text-right">
                {`$${formatDollars(props.quoteSummary.dollarCost)}`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="px-0" />
              <TableCell className="px-0" />
              <TableCell className="px-0 text-right">Total excluding GST</TableCell>
              <TableCell className="px-0 text-right">
                {`$${formatDollars(calculateTaxExcluded(props.quoteSummary.dollarCost))}`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="px-0" />
              <TableCell className="px-0" />
              <TableCell className="px-0 text-right">GST 10%</TableCell>
              <TableCell className="px-0 text-right">
                {`$${formatDollars(props.quoteSummary.dollarCost / 11)}`}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="px-0" />
              <TableCell className="px-0" />
              <TableCell className="px-0 text-right font-bold">Total Due</TableCell>
              <TableCell className="px-0 text-right">
                {`$${formatDollars(props.quoteSummary.dollarCost)}`}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>

        {submitExtractJob.isError && (
          <Alert variant="destructive">
            <AlertDescription>
              {(submitExtractJob.error as ReactQueryMutationError).message}
            </AlertDescription>
          </Alert>
        )}
        <PaymentMethodInfo style={{}} setPaymentMethod={setPaymentMethod} />
        <p className="text-xs text-muted-foreground">
          By confirming your payment, you allow Geoscape Australia to charge you
          for this payment and save your payment information in accordance with
          their terms.
        </p>
      </div>
      <div className="self-end pt-2 flex">
        <Button variant="ghost" onClick={() => props.setSubStep(0)}>
          Back
        </Button>
        <Button
          disabled={!paymentMethod || submitExtractJob.isPending}
          onClick={() => {
            submitExtractJob.mutate(props.clipConfig.id);
          }}
        >
          {submitExtractJob.isPending && (
            <Loader2 className="size-4 mr-2 animate-spin" />
          )}
          {`Pay $${formatDollars(props.quoteSummary.dollarCost)}`}
        </Button>
      </div>
    </>
  );
};
